/**
 * Local evidence vault — snapshots / recordings / uploads / dispatch rows
 * stay on this browser (IndexedDB). Soft≠Live: never claims server retention.
 */
const DB_NAME = "croc.cameras.local.v1";
const STORE = "evidence";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "id" });
        os.createIndex("source_id", "source_id", { unique: false });
        os.createIndex("at", "at", { unique: false });
        os.createIndex("kind", "kind", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB open failed"));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("IndexedDB tx failed"));
    tx.onabort = () => reject(tx.error || new Error("IndexedDB tx aborted"));
  });
}

function normalizeKind(kind) {
  const k = String(kind || "audit").toLowerCase();
  if (k === "snapshot" || k === "recording" || k === "upload" || k === "dispatch" || k === "audit") {
    return k;
  }
  return "audit";
}

export async function saveLocalEvidence(row) {
  const loopId = String(row.loop_id || "");
  const eventId = String(row.event_id || loopId || "");
  const item = {
    id: String(row.id || (`loc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)),
    kind: normalizeKind(row.kind),
    source_id: String(row.source_id || ""),
    label: String(row.label || ""),
    integrity_mark: String(row.integrity_mark || ""),
    trigger_kind: String(row.trigger_kind || "unknown"),
    detail: String(row.detail || ""),
    blob: row.blob || null,
    mime: String(row.mime || ""),
    media_kind: normalizeKind(row.media_kind || row.kind),
    soft_frame_tone: String(row.soft_frame_tone || ""),
    loop_id: loopId,
    event_id: eventId,
    note: String(row.note || ""),
    author: String(row.author || ""),
    author_role: String(row.author_role || ""),
    ai_read: String(row.ai_read || ""),
    ai_fixed: !!row.ai_fixed,
    at: Number(row.at || Date.now()),
    retained_local: true,
  };
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put(item);
  await txDone(tx);
  db.close();
  return item;
}

export async function listLocalEvidence({ sourceId = "", kind = "", limit = 80 } = {}) {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const store = tx.objectStore(STORE);
  const req = sourceId
    ? store.index("source_id").getAll(String(sourceId))
    : store.getAll();
  const rows = await new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error || new Error("IndexedDB read failed"));
  });
  await txDone(tx);
  db.close();
  const wantKind = kind ? normalizeKind(kind) : "";
  return (rows || [])
    .filter((r) => !wantKind || normalizeKind(r.kind || r.media_kind) === wantKind)
    .slice()
    .sort((a, b) => Number(b.at || 0) - Number(a.at || 0))
    .slice(0, Math.max(1, Math.min(Number(limit) || 80, 200)));
}

export async function captureFrameToLocal({
  video, sourceId, label, triggerKind = "human", eventId = "", loopId = "",
}) {
  if (!video || typeof document === "undefined") {
    throw new Error("No video frame to capture");
  }
  const canvas = document.createElement("canvas");
  const w = Math.max(1, video.videoWidth || 640);
  const h = Math.max(1, video.videoHeight || 360);
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(video, 0, 0, w, h);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
  if (!blob) throw new Error("Frame encode failed");
  const at = Date.now();
  const integrity = `LOC-${String(sourceId || "x").slice(0, 8)}-${at.toString(36).toUpperCase()}`;
  const eid = String(eventId || loopId || (sourceId ? `trig:${sourceId}` : ""));
  return saveLocalEvidence({
    kind: "snapshot",
    media_kind: "snapshot",
    source_id: sourceId,
    label: label || sourceId,
    integrity_mark: integrity,
    trigger_kind: triggerKind,
    detail: "Saved on this device only",
    blob,
    mime: "image/jpeg",
    soft_frame_tone: "dock",
    loop_id: loopId || "",
    event_id: eid,
    at,
  });
}

/**
 * Save a local canvas copy, then ask the server to capture its own μDock frame.
 * Browser pixels never become server-authoritative evidence.
 */
export async function captureFrameToServer({
  video,
  apiPost,
  deviceId,
  cameraId = "",
  sourceId = "",
  sessionId = "",
  label = "",
  triggerKind = "human",
  eventId = "",
  loopId = "",
}) {
  const livePlaying = !!(video && video.videoWidth > 0 && video.videoHeight > 0);
  if (!livePlaying) {
    const err = new Error("live_frame_required");
    err.code = "live_frame_required";
    throw err;
  }
  if (typeof apiPost !== "function") {
    throw new Error("api_post_required");
  }
  const did = String(deviceId || "").trim();
  if (!did) throw new Error("device_id_required");

  const local = await captureFrameToLocal({
    video, sourceId: sourceId || did, label, triggerKind, eventId, loopId,
  });
  const server = await apiPost("/admin/camera-snapshots/live-capture", {
    device_id: did,
    camera_id: String(cameraId || "").trim(),
    session_id: String(sessionId || "").trim(),
    source_id: String(sourceId || local.source_id || "").trim(),
  });
  return { local, server };
}

export async function saveAuditLocal({
  id = "", sourceId, label, detail, triggerKind = "human",
  eventId = "", loopId = "", at = 0, author = "", authorRole = "",
  aiRead = "", aiFixed = false,
}) {
  return saveLocalEvidence({
    id: id || undefined,
    kind: "audit",
    media_kind: "audit",
    source_id: sourceId,
    label,
    integrity_mark: `AUD-${Date.now().toString(36).toUpperCase()}`,
    trigger_kind: triggerKind,
    detail,
    event_id: eventId,
    loop_id: loopId,
    author,
    author_role: authorRole,
    ai_read: aiRead,
    ai_fixed: aiFixed,
    at: Number(at || Date.now()),
  });
}

/** Persist dispatch loop narrative locally (idempotent by loop id). */
export async function saveDispatchLocal({ loop, sourceId, label }) {
  if (!loop || !loop.id) return null;
  const id = `disp_${String(loop.id)}`;
  const stages = Array.isArray(loop.stages) ? loop.stages : [];
  const doneN = stages.filter((s) => s.done).length;
  const route = Array.isArray(loop.route) ? loop.route : [];
  const routeText = route.map((wp) => `${wp.done ? "✓" : "·"}${wp.label || wp.id}`).join(" → ");
  const detail = [
    `state=${loop.state || "open"}`,
    `assignee=${loop.assignee || "—"}`,
    `responder=${loop.responder || "—"}`,
    `eta=${loop.elapsed_s ?? "—"}/${loop.eta_s ?? "—"}`,
    `result=${loop.loop_result || "open"}`,
    `stages=${doneN}/${stages.length}`,
    routeText ? `route=${routeText}` : "",
    loop.after_response ? `after=${loop.after_response}` : "",
  ].filter(Boolean).join(" · ");
  return saveLocalEvidence({
    id,
    kind: "dispatch",
    media_kind: "dispatch",
    source_id: sourceId || "",
    label: label || loop.assignee || loop.id,
    integrity_mark: `DSP-${String(loop.id).slice(0, 10)}`,
    trigger_kind: "dispatch",
    detail,
    soft_frame_tone: "field",
    loop_id: String(loop.id),
    event_id: String(loop.id),
    at: Date.now(),
  });
}

/** Mirror wall/soft evidence into local vault (no server claim; idempotent ids). */
export async function retainMediaEvidenceLocal(rows, {
  sourceId = "", fromLabel = "", eventId = "", loopId = "",
} = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const saved = [];
  const eid = String(eventId || loopId || (sourceId ? `trig:${sourceId}` : ""));
  for (const row of list) {
    if (!row) continue;
    const sid = String(row.snapshot_id || row.id || "");
    if (!sid) continue;
    const kind = normalizeKind(row.media_kind || row.kind || "snapshot");
    if (kind === "dispatch" || kind === "audit") continue;
    const item = await saveLocalEvidence({
      id: `ev_${sid}`,
      kind,
      media_kind: kind,
      source_id: sourceId || String(row.source_id || ""),
      label: fromLabel || row.from_label || row.label || sid,
      integrity_mark: row.integrity_mark || sid,
      trigger_kind: row.trigger_kind || "unknown",
      detail: String(row.trigger_origin || row.detail || "Retained on this device only"),
      soft_frame_tone: row.soft_frame_tone || "gate",
      loop_id: String(loopId || row.loop_id || ""),
      event_id: String(row.event_id || eid),
      at: Number(row.at || Date.now()),
    });
    saved.push(item);
  }
  return saved;
}
